/*
 * Copyright (c) 2024 NOOGA DEVELOPMENT, All rights reserved.
 * Author Julia Romanovskaya <julia.romanovskaya@noogadev.com>
 */

import {
    RoughCashboxesListDto,
    RoughCashboxesListItemDto,
    RoughCashboxesListFilterParams,
    CreateCashBoxTaskRequestParams,
    CreateCashBoxTaskResponse,
    CashBoxTasksListDto,
    DeviceServiceListItem,
    InitialisationOperations,
    SuitableTargetVersionsParams,
    SuitableTargetVersionsForDevicePlatform,
    CashBoxTaskType,
} from '../api/service/serviceClient.typings';
import { createAction } from '../../utils/redux-utils';
import { Notification, NOTIFICATION_TYPES } from '../../utils/utils';
import { i18n } from '../translation/i18n';
import { IFileWithMeta } from 'react-dropzone-uploader';
import { ServiceOperations } from '../../components/Service/ServiceTabs/ServiceTab/serviceTabConfig';

export enum ServiceActionType {
    OPEN_SERVICE_PAGE = 'ServicePage/ open page',
    EXIT_SERVICE_PAGE = 'ServicePage/ exit page',
    CHANGE_TAB = 'ServicePage/ change tab',

    OPEN_ADD_CASHBOXES_DIALOG = 'ServicePage/ open add cashboxes dialog',
    CLOSE_ADD_CASHBOXES_DIALOG = 'ServicePage/ close add cashboxes dialog',
    FETCH_ROUGH_CASHBOXES_LIST_NEXT_PAGE = 'ServicePage/ fetch rough cashboxes list next page',
    APPLY_ROUGH_CASHBOXES_LIST_FILTERS = 'ServicePage/ apply rough cashboxes list filters',
    CHANGE_ROUGH_CASHBOXES_LIST_FILTERS = 'ServicePage/ change rough cashboxes list filters',
    FETCH_ROUGH_CASHBOXES_LIST_PAGE_SUCCESS = 'ServicePage/ fetch rough cashboxes list page success',
    FETCH_ROUGH_CASHBOXES_LIST_PAGE_ERROR = 'ServicePage/ fetch rough cashboxes list page error',

    CREATE_CASHBOXES_TASKS = 'ServicePage/ create cashboxes tasks',
    CREATE_CASHBOXES_TASKS_SUCCESS = 'ServicePage/ create cashboxes tasks success',
    CREATE_CASHBOXES_TASKS_ERROR = 'ServicePage/ create cashboxes tasks error',

    GET_CASHBOXES_TASKS_STATUSES = 'ServicePage/ get cashboxes tasks statuses',
    GET_CASHBOXES_TASKS_STATUSES_SUCCESS = 'ServicePage/ get cashboxes tasks statuses success',
    GET_CASHBOXES_TASKS_STATUSES_ERROR = 'ServicePage/ get cashboxes tasks statuses errors',

    ADD_CASHBOXES_FOR_SERVICE = 'ServicePage/ add cashboxes to the service list',
    DELETE_ALL_CASHBOXES_FOR_SERVICE = 'ServicePage/ delete all cashboxes from the service list',
    DELETE_CASHBOX_FOR_SERVICE = 'ServicePage/ delete a cashbox from the service list',
    SELECT_ALL_CASHBOXES_FOR_SERVICE = 'ServicePage/ select all cashboxes from the service list',
    SELECT_CASHBOX_FOR_SERVICE = 'ServicePage/ select a cashbox from the service list',

    UPDATE_SERVICE_CASHBOXES_DATA = 'ServicePage/ update cashboxes data in service list',
    UPDATE_SERVICE_CASHBOXES_DATA_SUCCESS = 'ServicePage/ update cashboxes data in service list success',
    UPDATE_SERVICE_CASHBOXES_DATA_ERROR = 'ServicePage/ update cashboxes data in service list error',

    FETCH_SUITABLE_FW_TARGET_VERSIONS = 'ServicePage/ fetch suitable fw target versions',
    FETCH_SUITABLE_FW_TARGET_VERSIONS_SUCCESS = 'ServicePage/ fetch suitable fw target versions success',
    FETCH_SUITABLE_FW_TARGET_VERSIONS_ERROR = 'ServicePage/ fetch suitable fw target versions error',

    DOWNLOAD_CASHBOX_LOGS = 'ServicePage/ download cashbox logs',
    DOWNLOAD_CASHBOX_LOGS_SUCCESS = 'ServicePage/ download cashbox logs success',
    DOWNLOAD_CASHBOX_LOGS_ERROR = 'ServicePage/ download cashbox logs error',

    UPLOAD_FILE_START = 'ServicePage/ upload file for initialization start',
    UPLOAD_FILE_SUCCESS = 'ServicePage/ upload file for initialization success',
    UPLOAD_FILE_ERROR = 'ServicePage/ upload file for initialization error',

    FETCH_FACTORY_NUMBER_LIST = 'ServicePage/ fetch factory numbers list',
    FETCH_FACTORY_NUMBER_LIST_SUCCESS = 'ServicePage/ fetch factory numbers list success',
    FETCH_FACTORY_NUMBER_LIST_ERROR = 'ServicePage/ fetch factory numbers list error',

    FETCH_DEVICES_LIST = 'ServicePage/ fetch devices list',
    FETCH_DEVICES_LIST_SUCCESS = 'ServicePage/ fetch devices list success',
    FETCH_DEVICES_LIST_ERROR = 'ServicePage/ fetch devices list error',

    ADD_DEVICE_TO_LIST = 'ServicePage/ add device to list',
    DELETE_DEVICES_FROM_LIST = 'ServicePage/ delete devices from list',

    SELECT_DEVICE_FROM_TABLE = 'ServicePage/ select device from table',

    CLOSE_ADD_DEVICE_MODAL_LIST = 'ServicePage/ close add device to list modal',

    PERFORM_DEVICES_OPERATION = 'ServicePage/ perform devices operation',
    PERFORM_DEVICES_OPERATION_SUCCESS = 'ServicePage/ perform devices operation success',
    PERFORM_DEVICES_OPERATION_ERROR = 'ServicePage/ perform devices operation error',
}

export const openServicePageAction = () => createAction(ServiceActionType.OPEN_SERVICE_PAGE);

export const exitServicePageAction = () => createAction(ServiceActionType.EXIT_SERVICE_PAGE);

export const changeTabAction = (value: number) => createAction(ServiceActionType.CHANGE_TAB, value);

//-------------------------------cashbox services----------------------------------------------

export const openAddCashboxesDialogAction = () => createAction(ServiceActionType.OPEN_ADD_CASHBOXES_DIALOG);

export const closeAddCashboxesDialogAction = () => createAction(ServiceActionType.CLOSE_ADD_CASHBOXES_DIALOG);

export const fetchRoughCashboxesListNextPageAction = () =>
    createAction(ServiceActionType.FETCH_ROUGH_CASHBOXES_LIST_NEXT_PAGE);

export const applyRoughCashboxesListFiltersAction = () =>
    createAction(ServiceActionType.APPLY_ROUGH_CASHBOXES_LIST_FILTERS);

export const changeRoughCashboxesListFiltersAction = (params: RoughCashboxesListFilterParams) =>
    createAction(ServiceActionType.CHANGE_ROUGH_CASHBOXES_LIST_FILTERS, params);

export const fetchRoughCashboxesListPageSuccessAction = (list: RoughCashboxesListDto) =>
    createAction(ServiceActionType.FETCH_ROUGH_CASHBOXES_LIST_PAGE_SUCCESS, {
        list,
        notification:
            list.cashBoxes.length === 0
                ? {
                      type: NOTIFICATION_TYPES[1],
                      message: i18n.t('errors.notFoundByParams'),
                  }
                : null,
    });

export const fetchRoughCashboxesListPageErrorAction = (notification: Notification) =>
    createAction(ServiceActionType.FETCH_ROUGH_CASHBOXES_LIST_PAGE_ERROR, { notification });

export const createCashboxesTasksAction = (payload: {
    taskType: CashBoxTaskType;
    params: CreateCashBoxTaskRequestParams[];
}) => createAction(ServiceActionType.CREATE_CASHBOXES_TASKS, payload);

export const createCashboxesTasksSuccessAction = (payload: {
    taskType: CashBoxTaskType;
    response: CreateCashBoxTaskResponse[];
}) =>
    createAction(ServiceActionType.CREATE_CASHBOXES_TASKS_SUCCESS, {
        taskType: payload.taskType,
        response: payload.response,
    });

export const createCashboxesTasksErrorAction = (notification: Notification) =>
    createAction(ServiceActionType.CREATE_CASHBOXES_TASKS_ERROR, { notification });

export const getCashboxesTasksStatusesAction = (taskType: CashBoxTaskType) =>
    createAction(ServiceActionType.GET_CASHBOXES_TASKS_STATUSES, taskType);

export const getCashboxesTasksStatusesSuccessAction = (payload: CashBoxTasksListDto) =>
    createAction(ServiceActionType.GET_CASHBOXES_TASKS_STATUSES_SUCCESS, payload);

export const getCashboxesTasksStatusesErrorAction = (notification: Notification) =>
    createAction(ServiceActionType.GET_CASHBOXES_TASKS_STATUSES_ERROR, { notification });

export const addCashboxesForServiceAction = (cashboxes: RoughCashboxesListItemDto[]) =>
    createAction(ServiceActionType.ADD_CASHBOXES_FOR_SERVICE, cashboxes);

export const deleteAllCashboxesForServiceAction = () =>
    createAction(ServiceActionType.DELETE_ALL_CASHBOXES_FOR_SERVICE);

export const deleteCashboxForServiceAction = (taxNumber: string) =>
    createAction(ServiceActionType.DELETE_CASHBOX_FOR_SERVICE, taxNumber);

export const selectAllCashboxesForServiceAction = (isSelected: boolean) =>
    createAction(ServiceActionType.SELECT_ALL_CASHBOXES_FOR_SERVICE, isSelected);

export const selectCashboxForServiceAction = (taxNumber: string, isSelected: boolean) =>
    createAction(ServiceActionType.SELECT_CASHBOX_FOR_SERVICE, { taxNumber, isSelected });

export const updateServiceCashboxesDataAction = () => createAction(ServiceActionType.UPDATE_SERVICE_CASHBOXES_DATA);

export const updateServiceCashboxesDataSuccessAction = (data: RoughCashboxesListDto) =>
    createAction(ServiceActionType.UPDATE_SERVICE_CASHBOXES_DATA_SUCCESS, data);

export const updateServiceCashboxesDataErrorAction = (notification: Notification) =>
    createAction(ServiceActionType.UPDATE_SERVICE_CASHBOXES_DATA_ERROR, { notification });

export const fetchSuitableFwTargetVersionsAction = (params: SuitableTargetVersionsParams) =>
    createAction(ServiceActionType.FETCH_SUITABLE_FW_TARGET_VERSIONS, params);

export const fetchSuitableFwTargetVersionsSuccessAction = (payload: SuitableTargetVersionsForDevicePlatform) =>
    createAction(ServiceActionType.FETCH_SUITABLE_FW_TARGET_VERSIONS_SUCCESS, payload);

export const fetchSuitableFwTargetVersionsErrorAction = (notification: Notification) =>
    createAction(ServiceActionType.FETCH_SUITABLE_FW_TARGET_VERSIONS_ERROR, { notification });

export const downloadCashBoxLogsAction = (taskId: string, fileName: string) =>
    createAction(ServiceActionType.DOWNLOAD_CASHBOX_LOGS, { logId: taskId, fileName });

export const downloadCashBoxLogsSuccessAction = () => createAction(ServiceActionType.DOWNLOAD_CASHBOX_LOGS_SUCCESS);

export const downloadCashBoxLogsErrorAction = (notification: Notification) =>
    createAction(ServiceActionType.DOWNLOAD_CASHBOX_LOGS_ERROR, { notification });

//-------------------------------device services----------------------------------------------

export const uploadFileStartAction = (fileMetaData: IFileWithMeta, operation: InitialisationOperations) =>
    createAction(ServiceActionType.UPLOAD_FILE_START, { fileMetaData, operation });

export const uploadFileSuccessAction = (notification: Notification, file: IFileWithMeta) => {
    file.remove();
    return createAction(ServiceActionType.UPLOAD_FILE_SUCCESS, { notification });
};

export const uploadFileErrorAction = (notification: Notification) =>
    createAction(ServiceActionType.UPLOAD_FILE_ERROR, { notification });

export const fetchFactoryNumbersListAction = (factoryNumberSubstring: string) => {
    return createAction(ServiceActionType.FETCH_FACTORY_NUMBER_LIST, factoryNumberSubstring);
};

export const fetchFactoryNumbersListSuccessAction = (factoryNumbers: string[]) => {
    return createAction(ServiceActionType.FETCH_FACTORY_NUMBER_LIST_SUCCESS, factoryNumbers);
};

export const fetchFactoryNumbersListErrorAction = (notification: Notification) => {
    return createAction(ServiceActionType.FETCH_FACTORY_NUMBER_LIST_ERROR, { notification });
};

export const fetchDevicesListAction = () => {
    return createAction(ServiceActionType.FETCH_DEVICES_LIST);
};

export const fetchDevicesListSuccessAction = (devicesList: DeviceServiceListItem[]) => {
    return createAction(ServiceActionType.FETCH_DEVICES_LIST_SUCCESS, devicesList);
};

export const fetchDevicesListErrorAction = (notification: Notification) => {
    return createAction(ServiceActionType.FETCH_DEVICES_LIST_ERROR, { notification });
};

export const addDeviceToListAction = (factoryNumber: string) => {
    return createAction(ServiceActionType.ADD_DEVICE_TO_LIST, factoryNumber);
};

export const deleteDevicesFromList = (factoryNumbers: string[]) => {
    return createAction(ServiceActionType.DELETE_DEVICES_FROM_LIST, factoryNumbers);
};

export const closeAddDeviceModalAction = () => {
    return createAction(ServiceActionType.CLOSE_ADD_DEVICE_MODAL_LIST);
};

export const selectDeviceFromTableAction = (factoryNumbers: string[]) => {
    return createAction(ServiceActionType.SELECT_DEVICE_FROM_TABLE, factoryNumbers);
};

export const performDevicesOperationAction = (operationType: ServiceOperations, factoryNumbers: string[]) => {
    return createAction(ServiceActionType.PERFORM_DEVICES_OPERATION, { operationType, factoryNumbers });
};

export const performDevicesOperationSuccessAction = (notification: Notification) => {
    return createAction(ServiceActionType.PERFORM_DEVICES_OPERATION_SUCCESS, { notification });
};

export const performDevicesOperationErrorAction = (notification: Notification) => {
    return createAction(ServiceActionType.PERFORM_DEVICES_OPERATION_ERROR, { notification });
};

export type ServiceActions =
    | ReturnType<typeof openServicePageAction>
    | ReturnType<typeof exitServicePageAction>
    | ReturnType<typeof changeTabAction>
    | ReturnType<typeof openAddCashboxesDialogAction>
    | ReturnType<typeof closeAddCashboxesDialogAction>
    | ReturnType<typeof fetchRoughCashboxesListNextPageAction>
    | ReturnType<typeof applyRoughCashboxesListFiltersAction>
    | ReturnType<typeof changeRoughCashboxesListFiltersAction>
    | ReturnType<typeof fetchRoughCashboxesListPageSuccessAction>
    | ReturnType<typeof fetchRoughCashboxesListPageErrorAction>
    | ReturnType<typeof createCashboxesTasksAction>
    | ReturnType<typeof createCashboxesTasksSuccessAction>
    | ReturnType<typeof createCashboxesTasksErrorAction>
    | ReturnType<typeof getCashboxesTasksStatusesAction>
    | ReturnType<typeof getCashboxesTasksStatusesSuccessAction>
    | ReturnType<typeof getCashboxesTasksStatusesErrorAction>
    | ReturnType<typeof addCashboxesForServiceAction>
    | ReturnType<typeof deleteAllCashboxesForServiceAction>
    | ReturnType<typeof deleteCashboxForServiceAction>
    | ReturnType<typeof selectAllCashboxesForServiceAction>
    | ReturnType<typeof selectCashboxForServiceAction>
    | ReturnType<typeof updateServiceCashboxesDataAction>
    | ReturnType<typeof updateServiceCashboxesDataSuccessAction>
    | ReturnType<typeof updateServiceCashboxesDataErrorAction>
    | ReturnType<typeof fetchSuitableFwTargetVersionsAction>
    | ReturnType<typeof fetchSuitableFwTargetVersionsSuccessAction>
    | ReturnType<typeof fetchSuitableFwTargetVersionsErrorAction>
    | ReturnType<typeof downloadCashBoxLogsAction>
    | ReturnType<typeof downloadCashBoxLogsSuccessAction>
    | ReturnType<typeof downloadCashBoxLogsErrorAction>
    | ReturnType<typeof uploadFileStartAction>
    | ReturnType<typeof uploadFileSuccessAction>
    | ReturnType<typeof uploadFileErrorAction>
    | ReturnType<typeof fetchFactoryNumbersListAction>
    | ReturnType<typeof fetchFactoryNumbersListSuccessAction>
    | ReturnType<typeof fetchFactoryNumbersListErrorAction>
    | ReturnType<typeof fetchDevicesListAction>
    | ReturnType<typeof fetchDevicesListSuccessAction>
    | ReturnType<typeof fetchDevicesListErrorAction>
    | ReturnType<typeof addDeviceToListAction>
    | ReturnType<typeof deleteDevicesFromList>
    | ReturnType<typeof closeAddDeviceModalAction>
    | ReturnType<typeof selectDeviceFromTableAction>
    | ReturnType<typeof performDevicesOperationAction>
    | ReturnType<typeof performDevicesOperationSuccessAction>
    | ReturnType<typeof performDevicesOperationErrorAction>;
